<template>
  <div class="dashboard-kpis">
    <!-- Fila 1: KPIs principales -->
    <div class="kpi-grid-primary">
      <KpiCard
        label="Facturado"
        :value="kpis.total_invoiced.value"
        :previous="kpis.total_invoiced.previous"
        :change="kpis.total_invoiced.change"
        :direction="kpis.total_invoiced.direction"
        prefix="$"
        :loading="loading"
        tooltip="Total facturado en el período seleccionado"
      />
      <KpiCard
        label="Pipeline abierto"
        :value="kpis.open_pipeline.value"
        :previous="kpis.open_pipeline.previous"
        :change="kpis.open_pipeline.change"
        :direction="kpis.open_pipeline.direction"
        prefix="$"
        :loading="loading"
        tooltip="Valor total de cotizaciones activas pendientes de cierre"
      />
      <KpiCard
        label="Conversión"
        :value="kpis.conversion_rate.value"
        :previous="kpis.conversion_rate.previous"
        :change="kpis.conversion_rate.change"
        :direction="kpis.conversion_rate.direction"
        prefix="%"
        :loading="loading"
        tooltip="Porcentaje de cotizaciones que se convirtieron en facturas"
      />
    </div>

    <!-- Fila 2: KPIs secundarios -->
    <div class="kpi-grid-secondary">
      <KpiSecondaryCard
        label="Ticket promedio"
        :value="kpis.average_ticket.value"
        :previous="kpis.average_ticket.previous"
        :change="kpis.average_ticket.change"
        :direction="kpis.average_ticket.direction"
        prefix="$"
        icon="el-icon-price-tag"
        iconColor="#31355c"
        iconBg="#e8ecf5"
        tooltip="Monto promedio por factura emitida"
      />
      <KpiSecondaryCard
        label="Clientes activos"
        :value="kpis.active_clients.value"
        :previous="kpis.active_clients.previous"
        :change="kpis.active_clients.change"
        :direction="kpis.active_clients.direction"
        icon="el-icon-user"
        iconColor="#3fdbbf"
        iconBg="#e4fff4"
        tooltip="Clientes con al menos una factura en el período"
      />
      <KpiSecondaryCard
        label="Cierre promedio"
        :value="kpis.avg_close_days.value"
        :previous="kpis.avg_close_days.previous"
        :change="kpis.avg_close_days.change"
        :direction="kpis.avg_close_days.direction"
        prefix="días"
        icon="el-icon-timer"
        iconColor="#ba7517"
        iconBg="#fff8e6"
        tooltip="Días promedio desde cotización hasta factura"
      />
    </div>

    <!-- Fila 3: KPIs de cobro — renderizados externamente si hideCollection -->
    <div v-if="!hideCollection" class="kpi-grid-collection">
      <template v-if="collectionTrackingEnabled">
        <KpiSecondaryCard
          label="Cobros pendientes"
          :value="kpis.collection_rate.pending_amount"
          prefix="$"
          icon="el-icon-money"
          iconColor="#534ab7"
          iconBg="#ece8ff"
          tooltip="Monto total pendiente de cobro"
          :subtextLine="collectionSubtext"
          :clickable="kpis.collection_rate.pending_amount > 0"
          @click="navigatePending"
        />
        <KpiSecondaryCard
          label="Facturas vencidas"
          :value="kpis.overdue_invoices.value"
          prefix="$"
          icon="el-icon-warning-outline"
          iconColor="#e24b4a"
          iconBg="#fff0f0"
          :danger="kpis.overdue_invoices.count > 0"
          tooltip="Facturas pendientes de pago que superaron su fecha de vencimiento"
          :subtextLine="overdueSubtext"
          :clickable="
            kpis.overdue_invoices.count > 0 || kpis.overdue_invoices.value > 0
          "
          @click="navigateOverdue"
        />
      </template>
      <template v-else-if="!loading">
        <div class="kpi-collection-inactive" @click="goToCollectionSettings">
          <i class="el-icon-money kpi-collection-inactive__icon"></i>
          <div class="kpi-collection-inactive__text">
            <span class="kpi-collection-inactive__title"
              >Seguimiento de cobros inactivo</span
            >
            <span class="kpi-collection-inactive__link"
              >Activar en Configuraciones →</span
            >
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import KpiCard from "./KpiCard";
import KpiSecondaryCard from "./KpiSecondaryCard";
import dashboardService from "../services/dashboardService";

const emptyKpi = { value: 0, previous: 0, change: 0, direction: "neutral" };

export default {
  name: "DashboardKpis",
  components: { KpiCard, KpiSecondaryCard },
  props: {
    period: { type: String, default: "month" },
    hideCollection: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: true,
      cache: {},
      reqGen: 0,
      collectionTrackingEnabled: false,
      kpis: {
        total_invoiced: { ...emptyKpi },
        open_pipeline: { ...emptyKpi, count: 0 },
        conversion_rate: { ...emptyKpi },
        average_ticket: { ...emptyKpi },
        active_clients: { ...emptyKpi },
        avg_close_days: { ...emptyKpi },
        overdue_invoices: { ...emptyKpi, count: 0, avg_days: 0 },
        collection_rate: {
          ...emptyKpi,
          paid_count: 0,
          sent_count: 0,
          pending_amount: 0
        }
      }
    };
  },
  computed: {
    overdueSubtext() {
      const o = this.kpis.overdue_invoices;
      if (o.count === 0) return "Todo al día";
      return o.count + " facturas vencidas";
    },
    collectionSubtext() {
      const c = this.kpis.collection_rate;
      if (c.pending_amount === 0 && c.sent_count > 0) return "Al día";
      if (c.sent_count === 0) return "";
      return (
        c.value +
        "% cobrado · " +
        c.paid_count +
        " de " +
        c.sent_count +
        " fact."
      );
    }
  },
  mounted() {
    this.$root.$on("dashboard-kpis-refresh", this.invalidateCache);
  },
  beforeDestroy() {
    this.$root.$off("dashboard-kpis-refresh", this.invalidateCache);
  },
  watch: {
    period: {
      immediate: true,
      handler(val) {
        this.fetchKpis(val);
      }
    }
  },
  methods: {
    navigateOverdue() {
      this.$emit("open-overdue-invoices");
    },
    navigatePending() {
      this.$emit("open-pending-collection");
    },
    goToCollectionSettings() {
      this.$router.push({
        name: "configurations",
        query: { tab: "collection" }
      });
    },
    invalidateCache() {
      this.cache = {};
      this.fetchKpis(this.period);
    },
    async fetchKpis(period) {
      // Marca esta llamada como la vigente — si una respuesta anterior
      // (p.ej. del negocio previo al cambiar de negocio) llega después,
      // se descarta en vez de pisar los datos correctos.
      const gen = ++this.reqGen;

      // Caché en memoria del componente
      if (this.cache[period]) {
        this.kpis = this.cache[period].kpis;
        this.collectionTrackingEnabled = this.cache[
          period
        ].collectionTrackingEnabled;
        return;
      }

      // Sin cache local → limpiar también el cache del servicio para garantizar datos frescos
      dashboardService.invalidateSummary(period);

      this.loading = true;
      try {
        const res = await dashboardService.getKpis({
          params: { period }
        });
        if (gen !== this.reqGen) return;
        if (res.data && res.data.success) {
          const defaults = {
            total_invoiced: { ...emptyKpi },
            open_pipeline: { ...emptyKpi, count: 0 },
            conversion_rate: { ...emptyKpi },
            average_ticket: { ...emptyKpi },
            active_clients: { ...emptyKpi },
            avg_close_days: { ...emptyKpi },
            overdue_invoices: { ...emptyKpi, count: 0, avg_days: 0 },
            collection_rate: {
              ...emptyKpi,
              paid_count: 0,
              sent_count: 0,
              pending_amount: 0
            }
          };
          this.kpis = { ...defaults, ...res.data.data.kpis };
          // Leer si el seguimiento de cobros está activo para este negocio
          this.collectionTrackingEnabled = !!res.data.data
            .collection_tracking_enabled;
          this.cache[period] = {
            kpis: this.kpis,
            collectionTrackingEnabled: this.collectionTrackingEnabled
          };
          this.$emit("collection-data", {
            collectionRate: this.kpis.collection_rate,
            overdueInvoices: this.kpis.overdue_invoices,
            collectionSubtext: this.collectionSubtext,
            overdueSubtext: this.overdueSubtext,
            trackingEnabled: this.collectionTrackingEnabled
          });
        }
      } catch (e) {
        // Mantener valores actuales
      } finally {
        if (gen === this.reqGen) this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.dashboard-kpis {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.kpi-grid-primary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.kpi-grid-secondary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.kpi-grid-collection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 1024px) {
  .kpi-grid-primary {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 768px) {
  .kpi-grid-primary,
  .kpi-grid-secondary {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 480px) {
  .kpi-grid-primary,
  .kpi-grid-secondary,
  .kpi-grid-collection {
    grid-template-columns: 1fr;
  }
}

// Estado inactivo: seguimiento de cobros desactivado
.kpi-collection-inactive {
  grid-column: 1 / -1;
  background: #fafbfc;
  border: 1.5px dashed #e4eaf6;
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #3ecfb4;
    background: #f5fdfb;
  }

  &__icon {
    font-size: 22px;
    color: #c0c4cc;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: 13px;
    font-weight: 500;
    color: #909399;
  }

  &__link {
    font-size: 12px;
    color: #3ecfb4;
    font-weight: 500;
  }
}
</style>
